import axios from "axios";
import type {
  Service,
  Environment,
  EnvironmentVariable,
  VariableHistory,
  User,
  RuntimeConfigFields,
  ServiceRuntimeConfig,
  ServiceEnvOverride,
  HeraldTask,
  StoredFile,
  FileVisibility,
  ServiceApiKey,
} from "../types";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api",
});

// Add token to requests if it exists
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (email: string, password: string) =>
    api.post("/auth/login", { email, password }),
  updateProfile: (data: {
    firstName?: string;
    lastName?: string;
    password?: string;
    mothersName?: string;
    fathersName?: string;
    phone?: string;
    permanentAddress?: string;
    currentAddress?: string;
    accountNumber?: string;
    ifsc?: string;
    accountHolderName?: string;
    aadharNumber?: string;
    panNumber?: string;
  }) => api.patch("/auth/profile", data),
  getProfile: () => api.get("/auth/profile"),
};

export const servicesApi = {
  list: () => api.get<Service[]>("/services"),
  create: (data: { name: string; description?: string }) =>
    api.post<Service>("/services", data),
  get: (id: string) => api.get<Service>(`/services/${id}`),
  update: (id: string, data: { description?: string }) =>
    api.patch<Service>(`/services/${id}`, data),
  delete: (id: string) => api.delete(`/services/${id}`),
};

export const environmentsApi = {
  list: (serviceId: string) =>
    api.get<Environment[]>(`/services/${serviceId}/environments`),
  create: (
    serviceId: string,
    data: { name: string; description?: string; disabled?: boolean }
  ) => api.post<Environment>(`/services/${serviceId}/environments`, data),
  get: (serviceId: string, envId: string) =>
    api.get<Environment>(`/services/${serviceId}/environments/${envId}`),
  update: (
    serviceId: string,
    envId: string,
    data: { description?: string; disabled?: boolean }
  ) =>
    api.patch<Environment>(
      `/services/${serviceId}/environments/${envId}`,
      data
    ),
  toggleDisabled: (serviceId: string, envId: string, disabled: boolean) =>
    api.patch<Environment>(
      `/services/${serviceId}/environments/${envId}/toggle-disabled`,
      { disabled }
    ),
  delete: (serviceId: string, envId: string) =>
    api.delete(`/services/${serviceId}/environments/${envId}`),
};

export const runtimeConfigApi = {
  getDefaults: (serviceId: string) =>
    api.get<ServiceRuntimeConfig | null>(`/services/${serviceId}/runtime-config`),
  updateDefaults: (serviceId: string, data: RuntimeConfigFields) =>
    api.put<ServiceRuntimeConfig>(`/services/${serviceId}/runtime-config`, data),
  getOverride: (serviceId: string, envId: string) =>
    api.get<ServiceEnvOverride | null>(
      `/services/${serviceId}/environments/${envId}/runtime-config`
    ),
  updateOverride: (serviceId: string, envId: string, data: RuntimeConfigFields) =>
    api.put<ServiceEnvOverride>(
      `/services/${serviceId}/environments/${envId}/runtime-config`,
      data
    ),
  deleteOverride: (serviceId: string, envId: string) =>
    api.delete(`/services/${serviceId}/environments/${envId}/runtime-config`),
};

export const usersApi = {
  list: () => api.get<User[]>("/users"),
  suspend: (id: string) => api.post(`/users/${id}/suspend`),
  activate: (id: string) => api.post(`/users/${id}/activate`),
  updateProfile: (data: { firstName: string; lastName: string }) =>
    api.patch("/users/profile", data),
  updatePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.patch("/users/password", data),
  changeRole: (userId: string, role: string) =>
    api.patch(`/users/${userId}/role`, { role }),
};

// Keycloak management (SUPERUSER only). Stateless passthrough to the
// Keycloak Admin REST API via the console backend.
export interface KcUser {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  emailVerified?: boolean;
  createdTimestamp?: number;
}

export interface KcGroup {
  id: string;
  name: string;
  path: string;
  subGroupCount?: number;
}

export interface KcGroupNode extends KcGroup {
  subGroups: KcGroup[];
}

export interface KcClientRoleRow {
  clientUuid: string;
  clientId: string;
  role: string;
  source: string;
}

export interface KcRole {
  id: string;
  name: string;
  description?: string;
  composite?: boolean;
  clientRole?: boolean;
}

export interface KcLinkCandidate {
  userId: string;
  email: string;
  name: string;
  keycloakUser: { id: string; username?: string; enabled?: boolean };
}

export const keycloakApi = {
  status: () => api.get<{ configured: boolean }>("/keycloak/status"),

  // Users
  listUsers: (search?: string) =>
    api.get<KcUser[]>("/keycloak/users", { params: { search, max: 100 } }),
  getUser: (id: string) => api.get<KcUser>(`/keycloak/users/${id}`),
  createUser: (data: {
    username: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    enabled?: boolean;
    password?: string;
    temporaryPassword?: boolean;
    sendInvite?: boolean;
    groupIds?: string[];
  }) =>
    api.post<{ id?: string; inviteSent: boolean; inviteError?: string; failedGroups: string[] }>(
      "/keycloak/users",
      data
    ),
  sendInvite: (id: string) => api.post(`/keycloak/users/${id}/send-invite`),
  updateUser: (
    id: string,
    data: Partial<{
      email: string;
      firstName: string;
      lastName: string;
      enabled: boolean;
    }>
  ) => api.put(`/keycloak/users/${id}`, data),
  linkCandidates: () =>
    api.get<KcLinkCandidate[]>("/keycloak/link-candidates"),
  confirmLink: (userId: string, keycloakUserId: string) =>
    api.post(`/keycloak/link-candidates/${userId}/confirm`, { keycloakUserId }),
  enableUser: (id: string) => api.post(`/keycloak/users/${id}/enable`),
  disableUser: (id: string) => api.post(`/keycloak/users/${id}/disable`),
  resetPassword: (id: string, password: string, temporary = true) =>
    api.post(`/keycloak/users/${id}/reset-password`, { password, temporary }),
  deleteUser: (id: string) => api.delete(`/keycloak/users/${id}`),
  getUserGroups: (id: string) =>
    api.get<KcGroup[]>(`/keycloak/users/${id}/groups`),
  getUserRealmRoles: (id: string) =>
    api.get<KcRole[]>(`/keycloak/users/${id}/realm-roles`),
  addUserRealmRoles: (id: string, roles: KcRole[]) =>
    api.post(`/keycloak/users/${id}/realm-roles`, { roles }),
  removeUserRealmRoles: (id: string, roles: KcRole[]) =>
    api.delete(`/keycloak/users/${id}/realm-roles`, { data: { roles } }),
  addUserToGroup: (userId: string, groupId: string) =>
    api.put(`/keycloak/users/${userId}/groups/${groupId}`),
  removeUserFromGroup: (userId: string, groupId: string) =>
    api.delete(`/keycloak/users/${userId}/groups/${groupId}`),

  // Groups
  listGroups: (search?: string) =>
    api.get<KcGroup[]>("/keycloak/groups", { params: { search } }),
  createGroup: (name: string) => api.post("/keycloak/groups", { name }),
  deleteGroup: (id: string) => api.delete(`/keycloak/groups/${id}`),
  groupTree: () => api.get<KcGroupNode[]>("/keycloak/groups/tree"),
  createSubgroup: (parentId: string, name: string) =>
    api.post(`/keycloak/groups/${parentId}/children`, { name }),
  getGroupClientRoles: (id: string) =>
    api.get<KcClientRoleRow[]>(`/keycloak/groups/${id}/client-roles`),
  addGroupClientRoles: (id: string, clientUuid: string, roles: KcRole[]) =>
    api.post(`/keycloak/groups/${id}/clients/${clientUuid}/roles`, { roles }),
  removeGroupClientRoles: (id: string, clientUuid: string, roles: KcRole[]) =>
    api.delete(`/keycloak/groups/${id}/clients/${clientUuid}/roles`, { data: { roles } }),
  founders: () => api.get<{ group: KcGroup | null }>("/keycloak/founders"),
  syncFounders: () =>
    api.post<{ group: KcGroup; granted: { clientId: string; roles: string[] }[] }>(
      "/keycloak/founders/sync"
    ),
  getGroupMembers: (id: string) =>
    api.get<KcUser[]>(`/keycloak/groups/${id}/members`),
  getGroupRealmRoles: (id: string) =>
    api.get<KcRole[]>(`/keycloak/groups/${id}/realm-roles`),
  addGroupRealmRoles: (id: string, roles: KcRole[]) =>
    api.post(`/keycloak/groups/${id}/realm-roles`, { roles }),
  removeGroupRealmRoles: (id: string, roles: KcRole[]) =>
    api.delete(`/keycloak/groups/${id}/realm-roles`, { data: { roles } }),

  // Realm roles
  listRoles: () => api.get<KcRole[]>("/keycloak/roles"),
  createRole: (name: string, description?: string) =>
    api.post("/keycloak/roles", { name, description }),
  deleteRole: (name: string) =>
    api.delete(`/keycloak/roles/${encodeURIComponent(name)}`),

  // Clients
  listClients: () =>
    api.get<
      {
        id: string;
        clientId: string;
        protocol: string;
        enabled: boolean;
        redirectUris: string[];
        baseUrl: string;
      }[]
    >("/keycloak/clients"),
  updateClient: (id: string, data: { redirectUris: string[]; baseUrl: string }) =>
    api.put(`/keycloak/clients/${id}`, data),
  getClientWebhook: (id: string) =>
    api.get<{ url: string; hasSecret: boolean }>(
      `/keycloak/clients/${id}/webhook`
    ),
  setClientWebhook: (id: string, data: { url: string; secret?: string }) =>
    api.put(`/keycloak/clients/${id}/webhook`, data),

  // Per-client roles
  listClientRoles: (clientUuid: string) =>
    api.get<KcRole[]>(`/keycloak/clients/${clientUuid}/roles`),
  createClientRole: (
    clientUuid: string,
    data: { name: string; description?: string; grantsAccess: boolean }
  ) => api.post(`/keycloak/clients/${clientUuid}/roles`, data),
  deleteClientRole: (clientUuid: string, roleName: string) =>
    api.delete(
      `/keycloak/clients/${clientUuid}/roles/${encodeURIComponent(roleName)}`
    ),

  // User <-> client-role mappings
  listClientMembers: (clientUuid: string) =>
    api.get<
      { userId: string; username: string; email: string; role: string; source: string }[]
    >(`/keycloak/clients/${clientUuid}/members`),
  listUserClientRoleMappings: (userId: string) =>
    api.get<KcClientRoleRow[]>(`/keycloak/users/${userId}/client-roles`),
  getUserClientRoles: (userId: string, clientUuid: string) =>
    api.get<KcRole[]>(`/keycloak/users/${userId}/clients/${clientUuid}/roles`),
  addUserClientRoles: (userId: string, clientUuid: string, roles: KcRole[]) =>
    api.post(`/keycloak/users/${userId}/clients/${clientUuid}/roles`, { roles }),
  removeUserClientRoles: (userId: string, clientUuid: string, roles: KcRole[]) =>
    api.delete(`/keycloak/users/${userId}/clients/${clientUuid}/roles`, {
      data: { roles },
    }),
  provisionClient: (data: {
    clientId: string;
    redirectUris: string[];
    baseUrl?: string;
    name?: string;
    restrictAccess: boolean;
    protocol?: "saml" | "oidc";
    dryRun?: boolean;
  }) =>
    api.post<{
      dryRun: boolean;
      clientId: string;
      protocol: "saml" | "oidc";
      // OIDC only, present in the create response once.
      clientSecret?: string;
      steps: string[];
      created: {
        id: string;
        gateRole: string;
        levelRoles: string[];
      } | null;
    }>("/keycloak/clients/provision", data),
  storeClientSecretInEnv: (
    clientUuid: string,
    data: { serviceId: string; environment: "DEV" | "PROD"; envName?: string }
  ) =>
    api.post<{ success: boolean; key: string; environment: "DEV" | "PROD"; replaced: boolean }>(
      `/keycloak/clients/${clientUuid}/secret-to-env`,
      data
    ),
  getSamlIdpConfig: () =>
    api.get<{
      ssoServiceUrl: string;
      signingCertificate: string | null;
    }>("/keycloak/saml-idp"),
};

export const serviceAccessApi = {
  requestAccess: (
    environmentId: string,
    accessType: "READ" | "WRITE",
    reason?: string
  ) =>
    api.post(`/service-access/request/${environmentId}`, {
      accessType,
      reason,
    }),
  respondToRequest: (requestId: string, status: "APPROVED" | "REJECTED") =>
    api.patch(`/service-access/request/${requestId}`, { status }),
  checkAccess: (environmentId: string) =>
    api.get<{ hasAccess: boolean; accessType?: "READ" | "WRITE" }>(
      `/service-access/${environmentId}/check`
    ),
  listRequests: () => api.get("/service-access/requests"),
};

export const githubAccessApi = {
  updateUsername: (githubUsername: string) =>
    api.patch("/github-access/username", { githubUsername }),
  validateUsername: (githubUsername: string) =>
    api.post<{ valid: boolean; error?: string; message?: string; user?: any }>(
      "/github-access/username/validate",
      { githubUsername }
    ),
  validateRepository: (repository: string) =>
    api.post<{
      valid: boolean;
      error?: string;
      message?: string;
      repository?: any;
    }>("/github-access/repository/validate", { repository }),
  getUsernameStatus: () =>
    api.get<{
      hasUsername: boolean;
      username?: string;
      canSetUsername: boolean;
    }>("/github-access/username"),
  requestAccess: (
    repository: string,
    accessType: "READ" | "WRITE",
    reason?: string
  ) => api.post("/github-access/request", { repository, accessType, reason }),
  respondToRequest: (requestId: string, status: "APPROVED" | "REJECTED") =>
    api.patch(`/github-access/request/${requestId}`, { status }),
  listRequests: () => api.get("/github-access/requests"),
};

export const deboardingApi = {
  initiate: (userId: string, reason?: string) =>
    api.post(`/deboarding/initiate/${userId}`, { reason }),
  executeNext: (processId: string) =>
    api.post(`/deboarding/execute/${processId}`),
  executePhase: (processId: string, phase: string) =>
    api.post(`/deboarding/execute/${processId}/${phase}`),
  getProgress: (processId: string) =>
    api.get(`/deboarding/progress/${processId}`),
  cancel: (processId: string) => api.post(`/deboarding/cancel/${processId}`),
  listProcesses: () => api.get("/deboarding/processes"),
  getProcess: (processId: string) =>
    api.get(`/deboarding/process/${processId}`),
};

export const deploymentsApi = {
  list: () => api.get<{ items: any[] }>(`/deployments`),
  refreshStatus: (payload?: { ids?: string[] }) =>
    api.post<{ items: any[] }>(`/deployments/refresh-status`, payload || {}),
  getLogs: (buildId: string) =>
    api.get<{ logs: string[] }>(`/deployments/${buildId}/logs`),
  triggerDeployment: (
    serviceId: string,
    envId: string,
    options?: { fullBuild?: boolean }
  ) => api.post(`/deployments/trigger/${serviceId}/${envId}`, options || {}),
  createProductionDeployments: (repository: string, commitSha: string) =>
    api.post(`/deployments/roll-deployments-to-prod`, {
      repository,
      commitSha,
    }),
  deleteCloudRunService: (serviceId: string, envId: string) =>
    api.delete(`/deployments/service/${serviceId}/${envId}`),
  getServices: () =>
    api.get<{ services: { id: string; name: string }[] }>(
      `/deployments/services`
    ),
  getRepositories: () =>
    api.get<{ repositories: string[] }>(`/github-access/repositories`),
  getBranches: (repoId: string) =>
    api.get<{ branches: { name: string; sha: string }[] }>(
      `/deployments/branches/${repoId}`
    ),
};

export const variablesApi = {
  list: (serviceId: string, envId: string) =>
    api.get<EnvironmentVariable[]>(
      `/services/${serviceId}/environments/${envId}/variables`
    ),
  create: (
    serviceId: string,
    envId: string,
    data: {
      key: string;
      value: string;
      isSecret?: boolean;
      isBuildTime?: boolean;
      description?: string;
    }
  ) =>
    api.post<EnvironmentVariable>(
      `/services/${serviceId}/environments/${envId}/variables`,
      data
    ),
  update: (
    serviceId: string,
    envId: string,
    variableId: string,
    data: {
      value: string;
      isSecret?: boolean;
      isBuildTime?: boolean;
      description?: string;
    }
  ) =>
    api.patch<EnvironmentVariable>(
      `/services/${serviceId}/environments/${envId}/variables/${variableId}`,
      data
    ),
  delete: (serviceId: string, envId: string, variableId: string) =>
    api.delete(
      `/services/${serviceId}/environments/${envId}/variables/${variableId}`
    ),
  getHistory: (serviceId: string, envId: string, variableId: string) =>
    api.get<VariableHistory[]>(
      `/services/${serviceId}/environments/${envId}/variables/${variableId}/history`
    ),
};

// Herald (prompt-to-code agent) task monitoring — proxied through this
// backend rather than calling Herald's own API directly from the browser.
export const heraldApi = {
  listTasks: () => api.get<HeraldTask[]>("/herald/tasks"),
  cleanupTask: (taskId: string) =>
    api.post<{ status: string; warning?: string }>(
      `/herald/tasks/${taskId}/cleanup`
    ),
  cancelTask: (taskId: string) =>
    api.post<{ status: string }>(`/herald/tasks/${taskId}/cancel`),
  deleteTask: (taskId: string) =>
    api.delete<{ status: string }>(`/herald/tasks/${taskId}`),
};

// File storage. Bytes go straight to storage via a signed URL; this backend
// only issues URLs and keeps metadata.
export const filesApi = {
  list: (params: {
    namespace?: string;
    visibility?: FileVisibility;
    search?: string;
    page: number;
    pageSize: number;
  }) =>
    api.get<{ items: StoredFile[]; total: number }>("/files", { params }),
  namespaces: () => api.get<{ namespaces: string[] }>("/files/namespaces"),
  get: (id: string, inline = false) =>
    api.get<StoredFile & { url: string; expiresAt: string | null }>(
      `/files/${id}`,
      { params: { inline } }
    ),
  delete: (id: string) => api.delete(`/files/${id}`),
  upload: async (
    file: File,
    options: { visibility: FileVisibility; namespace?: string; filename?: string },
    onProgress?: (percent: number) => void
  ) => {
    const contentType = file.type || "application/octet-stream";
    const { data } = await api.post<{
      file: StoredFile;
      upload: { method: string; url: string; headers: Record<string, string> };
    }>("/files", {
      contentType,
      size: file.size,
      ...options,
      filename: options.filename?.trim() || file.name,
    });
    // Plain axios: the signed URL must not carry our Authorization header.
    await axios.put(data.upload.url, file, {
      headers: data.upload.headers,
      onUploadProgress: (e) =>
        onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
    });
    return api.post<StoredFile>(`/files/${data.file.id}/complete`);
  },
};

export const apiKeysApi = {
  list: () => api.get<ServiceApiKey[]>("/api-keys"),
  create: (data: { name: string; namespace: string }) =>
    api.post<ServiceApiKey & { key: string }>("/api-keys", data),
  revoke: (id: string) => api.post<ServiceApiKey>(`/api-keys/${id}/revoke`),
};
